import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { IMAGES } from "../data/content";

const MODULES = [
  { n: "01", title: "Analyse & Bestand", copy: "Wir lesen das bestehende Objekt — Substanz, Nutzungsspuren, Grundriss, Licht, Materialität — und sortieren, was bleiben darf und was nicht." },
  { n: "02", title: "Konzept-Reset", copy: "Eine klare Stilserie wird gewählt, Ausbaustufe festgelegt. Der Raum bekommt wieder eine Haltung." },
  { n: "03", title: "Modulare Umsetzung", copy: "Refit kann vollständig oder modular erfolgen — nach Raum, Budget oder Jahreszeit gestaffelt." },
  { n: "04", title: "Übergabe & Betrieb", copy: "Das Objekt ist wieder vermiet- oder nutzungsbereit — mit klarer Geschichte und spürbarer Aufenthaltsqualität." },
];

const SCOPE = [
  "Komplettes Einrichtungs-Refit",
  "Partieller Refit einzelner Räume",
  "Textil- und Lichtkonzept neu",
  "Küche & Bad: Neugestaltung oder Aufwertung",
  "Staging für Vermietung oder Verkauf",
  "Vor-Ort-Aufnahme & Bestandsanalyse",
];

export default function Refit() {
  useDocumentTitle(
    "Refit — Ferienhaus renovieren & modernisieren",
    "Bestandsobjekte neu gelesen: Refit-Konzepte für Ferienhäuser und Ferienwohnungen. Vollständig oder modular, mit optionaler Vor-Ort-Aufnahme."
  );

  return (
    <div data-testid="page-refit">
      {/* Hero */}
      <section className="pt-28 sm:pt-32 md:pt-44 pb-20 md:pb-28">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <SectionLabel index="∞" text="Refit" />
            </Reveal>
            <Reveal delay={60}>
              <h1 className="display-xl mt-8 text-[44px] sm:text-[60px] md:text-[80px] lg:text-[96px] text-balance">
                Bestand,
                <br className="hidden sm:block" /> neu gelesen.
              </h1>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora prose-livinora--lead mt-9 max-w-xl">
                Refit ist mehr als Renovierung — es ist ein präzises Re-Reading Ihres Objekts.
                Aus Bestand entsteht ein schlüssiges, modernes, wiedervermarktbares Ganzes.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/konfigurator?object=refit"
                  className="btn-livinora btn-primary btn-magnetic"
                  data-testid="refit-primary-cta"
                >
                  Projekt konfigurieren <span>→</span>
                </Link>
                <Link to="/individualisierung" className="btn-livinora btn-ghost">
                  Vor-Ort-Planung
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal variant="mask">
              <div className="media-wrap aspect-[4/5]">
                <img src={IMAGES.refit} alt="Refit" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="section-pad bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] atmosphere-dark noise">
        <div className="container-livinora">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 items-end">
            <div className="md:col-span-7">
              <Reveal>
                <SectionLabel index="01" text="Vorgehen" inverse />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-8 leading-[1.02]">
                  Vier Module. Ein ruhiger Weg.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <Reveal delay={140}>
                <p className="text-[color:var(--livinora-stone)] leading-relaxed text-base md:text-[17px]">
                  Refits lassen sich selten „auf einmal" machen. Wir strukturieren den Prozess so,
                  dass Ihr Objekt in jeder Phase nutzbar bleibt.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-[color:var(--livinora-stone-deep)]/50">
            {MODULES.map((m, i) => (
              <Reveal key={m.n} delay={i * 80}>
                <div
                  className={`py-10 md:py-12 pr-6 md:pr-10 ${i !== 0 ? "md:pl-10" : ""} ${
                    i !== 3 ? "md:border-r border-[color:var(--livinora-stone-deep)]/40" : ""
                  } border-b md:border-b-0 border-[color:var(--livinora-stone-deep)]/40`}
                >
                  <div className="text-[color:var(--livinora-stone)] text-[10.5px] tracking-[0.32em]">{m.n}</div>
                  <h3 className="serif text-2xl md:text-3xl mt-3 leading-tight">{m.title}</h3>
                  <p className="mt-4 text-sm text-[color:var(--livinora-stone)] leading-relaxed">{m.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Scope */}
      <section className="section-pad">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel index="02" text="Leistungsumfang" />
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-4xl md:text-5xl mt-8 leading-[1.05]">
                Was ein Refit umfassen kann.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7 max-w-md">
                Je nach Objekt, Ziel und Zeitrahmen kombinieren wir Leistungen modular — vom
                kompletten Re-Setup bis zur partiellen Aufwertung.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-[color:var(--livinora-hairline)] border-y border-[color:var(--livinora-hairline)]">
              {SCOPE.map((s, i) => (
                <Reveal key={s} delay={i * 50}>
                  <li className="flex items-center justify-between py-5 gap-6">
                    <span className="flex items-center gap-6">
                      <span className="micro-label">0{i + 1}</span>
                      <span className="text-base sm:text-lg">{s}</span>
                    </span>
                    <span className="text-[color:var(--livinora-stone-deep)]">→</span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/kontakt" className="btn-livinora btn-primary btn-magnetic" data-testid="refit-scope-cta">
                Refit anfragen <span>→</span>
              </Link>
              <Link to="/shop?room=refit" className="btn-livinora btn-ghost">
                Refit-Raumsets
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
